import AccountBalance from '@mui/icons-material/AccountBalance';
import CompanyIcon from '@mui/icons-material/Business';
import CampaignIcon from '@mui/icons-material/Campaign';
import CoPresent from '@mui/icons-material/CoPresent';
import DocumentsIcon from '@mui/icons-material/Description';
import EmployeesIcon from '@mui/icons-material/Group';
import ContractsIcon from '@mui/icons-material/HistoryEdu';
import DashboardIcon from '@mui/icons-material/InsertChart';
import WithdrawalsIcon from '@mui/icons-material/Payments';
import DistributorIcon from '@mui/icons-material/Store';
import OperationsIcon from '@mui/icons-material/SwapVert';
import { match } from 'ts-pattern';

import { MenuItemsItem, SingleLevelItem } from '@sh4res/bo-common/components';

import { UseCompanyDetailsReturnType } from '@Hooks/useCompanyDetails';
import { TranslationKeys } from '@I18n/types';
import { BoExternalIdentityI } from '@Utils/providers/authProvider';

export const getMenuItems = (
  { auth_role, roles, companyId, contract_availability }: BoExternalIdentityI,
  company: UseCompanyDetailsReturnType,
): MenuItemsItem<TranslationKeys>[] => {
  // Build operations sub-items based on contracts
  const operationsSubItems: SingleLevelItem<TranslationKeys>[] =
    contract_availability.has_es_or_soles_contracts
      ? [
          {
            name: 'collective_operations' as const,
            label: 'menu.collective_operations' as const,
            icon: (
              <CampaignIcon
                style={{ opacity: 0, width: '22px', height: '22px' }}
                aria-hidden="true"
              />
            ),
          },
          {
            name: 'individual_operations',
            label: 'menu.individual_operations',
            icon: (
              <CoPresent aria-hidden="true" style={{ opacity: 0, width: '22px', height: '22px' }} />
            ),
          },
        ]
      : [];

  const menuItems: MenuItemsItem<TranslationKeys>[] = [
    {
      name: 'distributor',
      label: 'menu.distributor',
      icon: <DistributorIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
    },
    ...match(auth_role)
      .with(
        'b2b_admin',
        () =>
          [
            {
              name: 'dashboard',
              action: 'show',
              path: '/dashboard',
              label: 'menu.dashboard',
              icon: <DashboardIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
            },
          ] as const,
      )
      .with(
        'b2b_distributor',
        () =>
          [
            {
              name: 'companies',
              action: 'list',
              path: 'companies',
              label: 'menu.companies',
              icon: <CompanyIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
            },
          ] as const,
      )
      .with(
        'b2b_inspector',
        () =>
          [
            {
              name: 'companies',
              action: 'list',
              path: 'companies',
              label: 'menu.companies',
              icon: <CompanyIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
            },
          ] as const,
      )
      .exhaustive(),
  ];

  const adminRole = roles.find((role) => role.includes('admin'));

  if (adminRole === 'admin_level_4') {
    menuItems.push({
      name: 'companies',
      action: 'show',
      path: `companies/${String(companyId[0])}/show`,
      label: 'menu.company',
      icon: <CompanyIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
    });
  }

  if (auth_role !== 'b2b_admin' || adminRole !== 'admin_level_1') {
    menuItems.push({
      name: 'employees',
      label: 'menu.employees',
      icon: <EmployeesIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
    });
  }

  menuItems.push({
    name: 'contracts',
    label: 'menu.contracts',
    icon: <ContractsIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
  });

  if (company?.is_international) {
    menuItems.push({
      name: 'withdrawals',
      label: 'menu.withdrawals',
      icon: <WithdrawalsIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
    });
  }

  if (operationsSubItems.length > 0) {
    menuItems.push({
      name: 'operations',
      label: 'menu.operations',
      icon: <OperationsIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
      items: operationsSubItems,
    });
  }

  menuItems.push(
    {
      name: 'instruments',
      label: 'menu.instruments',
      icon: <AccountBalance aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
    },
    {
      name: 'companies/documents',
      path: '/documents',
      label: 'menu.documents',
      icon: <DocumentsIcon aria-hidden="true" style={{ width: '22px', height: '22px' }} />,
      customRoute: true,
    },
  );

  return menuItems;
};
